import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Certificate PDF Generation Service
 * Uses html2canvas with browser's native font rendering (supports OpenType features)
 */

import templateImage from '@/assets/certificate-template.png';

/**
 * Converts text to Title Case for proper script font rendering
 * Script fonts need lowercase letters to create connected flowing cursive
 */
function toTitleCase(str) {
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Calculates font size to fit name within certificate width
 */
function calculateNameFontSize(name) {
  const len = name.length;
  if (len <= 12) return 52;
  if (len <= 16) return 46;
  if (len <= 20) return 40;
  if (len <= 25) return 36;
  if (len <= 30) return 30;
  if (len <= 40) return 24;
  return 20;
}

/**
 * Calculates font size for the description text based on length
 */
function calculateDescFontSize(text) {
  const len = text.length;
  if (len <= 60) return 24;
  if (len <= 80) return 22;
  if (len <= 100) return 21;
  return 20;
}

/**
 * Generates and downloads a certificate PDF
 */
export async function generateCertificatePDF(certificateData) {
  const candidateName = toTitleCase(certificateData.candidateName || '');
  const nameFontSize = calculateNameFontSize(candidateName);

  // Build description line and calculate its font size
  const descLine = `CDC number : ${certificateData.cdcNumber || 'N/A'} for completion of ${certificateData.courseName || ''}`;
  const descFontSize = calculateDescFontSize(descLine);

  // Create the certificate element on screen for proper font rendering
  const container = document.createElement('div');
  container.id = 'temp-certificate-container';
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    z-index: -1;
    width: 1200px;
    background: white;
    opacity: 0;
    pointer-events: none;
  `;

  container.innerHTML = `
    <div id="cert-capture" style="position: relative; width: 1200px; background: white;">
      <img src="${templateImage}" alt="Certificate Template" style="width: 100%; height: auto; display: block;" crossorigin="anonymous" />
      <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;">

        <h1 style="
          position: absolute;
          top: 19%;
          left: 50%;
          transform: translateX(-50%);
          font-family: 'Playfair Display', serif;
          font-size: 34px;
          font-weight: 500;
          color: #1a4a6e;
          letter-spacing: 3px;
          text-align: center;
          margin: 0;
          white-space: nowrap;
        ">THIS CERTIFICATE IS</h1>

        <div style="
          position: absolute;
          top: 35%;
          left: 8%;
          right: 8%;
          text-align: center;
        ">
          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: 26px;
            font-style: italic;
            color: #1a4a6e;
            margin: 0 0 8px 0;
          ">Presented to</p>

          <h2 style="
            font-family: 'Great Vibes', cursive;
            font-size: ${nameFontSize * 1.33}px;
            color: #1a4a6e;
            margin: 0 0 12px 0;
            font-weight: 400;
            line-height: 1.2;
          ">${candidateName}</h2>

          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: ${descFontSize}px;
            color: #1a4a6e;
            margin: 4px 0;
            font-style: italic;
            font-weight: 500;
            padding: 0 20px;
          ">${descLine}</p>

          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: ${descFontSize}px;
            color: #1a4a6e;
            margin: 4px 0;
            font-style: italic;
            font-weight: 500;
          ">course on ${certificateData.completionDate || ''}.</p>
        </div>

        <div style="
          position: absolute;
          bottom: 14%;
          left: 10%;
          text-align: left;
          max-width: 350px;
        ">
          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: 17px;
            font-style: italic;
            color: #1a4a6e;
            margin: 1px 0;
            line-height: 1.3;
          ">This certificate is valid for a period of <span style="text-decoration: underline; font-weight: 600;">${certificateData.validityPeriod || '12 months'}</span></p>
          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: 17px;
            font-style: italic;
            color: #1a4a6e;
            margin: 1px 0;
            line-height: 1.3;
          ">from the date of issue.</p>
        </div>

        <div style="
          position: absolute;
          bottom: 14%;
          right: 10%;
          text-align: center;
        ">
          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: 18px;
            color: #1a4a6e;
            margin: 0 0 4px 0;
          ">${certificateData.presentedDate || ''}</p>
          <div style="
            width: 150px;
            height: 1px;
            background-color: #1a4a6e;
            margin: 0 auto 4px auto;
          "></div>
          <p style="
            font-family: 'Cormorant Garamond', serif;
            font-size: 16px;
            color: #1a4a6e;
            font-style: italic;
            margin: 0;
          ">Date Presented</p>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  try {
    // Wait for image to load
    const img = container.querySelector('img');
    await new Promise((resolve, reject) => {
      if (img.complete) resolve();
      else { img.onload = resolve; img.onerror = reject; }
    });

    // Wait for Great Vibes font to be fully loaded
    if (document.fonts) {
      await document.fonts.load(`400 ${nameFontSize}px "Great Vibes"`);
      await document.fonts.ready;
    }

    // Give browser time to paint with correct fonts
    await new Promise(resolve => setTimeout(resolve, 500));

    // Capture with html2canvas
    const certificateElement = container.querySelector('#cert-capture');
    const canvas = await html2canvas(certificateElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: 1200,
    });

    // Calculate dimensions for landscape A4
    const imgWidth = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const imgData = canvas.toDataURL('image/png', 1.0);
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

    // Generate filename
    const certNo = certificateData.certNo ? certificateData.certNo.replace(/\//g, '_') : '';
    const nameForFile = candidateName ? candidateName.replace(/\s+/g, '_') : 'certificate';
    const filename = certNo ? `certificate-${certNo}.pdf` : `certificate_${nameForFile}.pdf`;

    pdf.save(filename);
    return true;
  } catch (error) {
    console.error('Error generating certificate PDF:', error);
    throw error;
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Formats a date to ordinal format (e.g., "8th November, 2025")
 */
export function formatCertificateDate(date) {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'long' });
  const year = d.getFullYear();

  const suffix = getOrdinalSuffix(day);
  return `${day}${suffix} ${month}, ${year}`;
}

function getOrdinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default {
  generateCertificatePDF,
  formatCertificateDate,
};
